import { Badge, HStack, Text, Button } from '@chakra-ui/react';

type DiffNumberProps = {
  newValue?: number;
  oldValue?: number;
  precision?: number; // number of decimals to round before comparing/displaying
  onClick?: () => void; // optional click handler to make the text clickable
};

function roundTo(value: number, precision: number): number {
  const p = Math.max(0, precision);
  const factor = Math.pow(10, p);
  return Math.round(value * factor) / factor;
}

// Component to render quantity values which might be clickable
export function TextValue({
  value,
  onClick,
}: {
  value: number;
  onClick?: () => void;
}) {
  const textContent = (
    <Text fontSize="xs" fontWeight="bold">
      {value}
    </Text>
  );

  if (onClick) {
    return (
      <Button
        fontSize="xs"
        fontWeight="bold"
        variant="link"
        color="teal.400"
        _hover={{
          fontWeight: 'bold',
        }}
        onClick={onClick}
        p={0}
        h="auto"
        minH="auto"
        minW="auto"
      >
        {textContent}
      </Button>
    );
  }

  return textContent;
}

// eslint-disable-next-line complexity
export function DiffNumber({
  newValue,
  oldValue,
  precision = 2,
  onClick,
}: DiffNumberProps) {
  const hasNew = typeof newValue === 'number';
  const hasOld = typeof oldValue === 'number';

  if (!hasNew && !hasOld) return null;

  const rNew = hasNew ? roundTo(newValue as number, precision) : undefined;
  const rOld = hasOld ? roundTo(oldValue as number, precision) : undefined;

  // Decide primary value to show (prefer newValue when present)
  const primary = (rNew ?? rOld) as number;

  // If both present, compute diff after rounding
  if (typeof rNew === 'number' && typeof rOld === 'number') {
    const delta = roundTo(rNew - rOld, precision);
    if (delta === 0) {
      return <TextValue value={primary} onClick={onClick} />;
    }
    const isIncrease = delta > 0;
    const sign = isIncrease ? '+' : '-';
    const colorScheme = isIncrease ? 'green' : 'red';
    return (
      <HStack spacing={2} align="center">
        <TextValue value={primary} onClick={onClick} />
        <Badge
          colorScheme={colorScheme}
          textTransform={'none'}
          size="xs"
          fontSize={'xs'}
          borderRadius={'md'}
        >
          {sign}
          {Math.abs(delta)}
        </Badge>
      </HStack>
    );
  }

  // Only one value present
  return <TextValue value={primary} onClick={onClick} />;
}

export default DiffNumber;
